import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
} from 'recharts'
import {
  DollarSign, Truck, Package, RotateCcw, Calculator, Tag, Wallet,
  ChevronUp, ChevronDown, Search, FileSpreadsheet, ExternalLink,
  Trophy, AlertTriangle, RefreshCw, ListChecks, BarChart3, TrendingUp,
} from 'lucide-react'
import { api, formatMoney } from '@/lib/api'

type Linha = {
  item_id: number
  pedido_id: number
  pedido_numero: number
  ml_order_id: string | null
  data: string | null
  anuncio: string
  produto_nome: string
  ml_item_id: string | null
  sku: string
  conta: string | null
  modalidade: string | null
  valor_unit: number
  qtde: number
  faturamento: number
  custo: number
  imposto: number
  tarifa: number
  frete_comp: number
  frete_vend: number
  adiantamento: number
  mc: number
  mc_pct: number
  status: string
  cancelado: boolean
}
type Resp = {
  kpis: Record<string, number>
  modalidades: Record<string, number>
  donut: { label: string; valor: number; cor: string }[]
  linhas: Linha[]
  paginacao: { page: number; per_page: number; total: number; pages: number }
}

const MODALIDADE_LABEL: Record<string, string> = {
  fulfillment: 'Full',
  self_service: 'Flex',
  cross_docking: 'Coleta',
  drop_off: 'Agência',
  xd_drop_off: 'Agência (XD)',
}

function hojeISO() { return new Date().toISOString().slice(0, 10) }
function inicioMesISO() {
  const d = new Date(); d.setDate(1)
  return d.toISOString().slice(0, 10)
}

export default function ResumoFinanceiroMLPage() {
  const qc = useQueryClient()
  const [filtros, setFiltros] = useState({
    data_ini: inicioMesISO(),
    data_fim: hojeISO(),
    mlb: '',
    sku: '',
    status: '',
    modalidade: '',
    canal_id: '',
    pedido: '',
    considerar_frete_comp: false,
  })
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(50)
  const [sortKey, setSortKey] = useState<keyof Linha>('data')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [vista, setVista] = useState<'tabela' | 'ranking' | 'perdidos'>('tabela')

  const { data, isLoading, refetch } = useQuery<Resp>({
    queryKey: ['ml-resumo', filtros, page, perPage],
    queryFn: async () => {
      const params: any = { page, per_page: perPage }
      Object.entries(filtros).forEach(([k, v]) => { if (v !== '' && v !== false) params[k] = v })
      return (await api.get('/marketplace/ml/resumo-financeiro', { params })).data
    },
  })

  const { data: contas } = useQuery<{ id: number; identificador: string }[]>({
    queryKey: ['ml-contas'],
    queryFn: async () => (await api.get('/marketplace/ml/contas')).data,
  })

  const { data: ranking } = useQuery({
    queryKey: ['ml-ranking', filtros.data_ini, filtros.data_fim],
    queryFn: async () => (await api.get('/marketplace/ml/ranking-produtos', {
      params: { data_ini: filtros.data_ini, data_fim: filtros.data_fim, ordem: 'mc', limit: 100 },
    })).data,
    enabled: vista === 'ranking',
  })

  const { data: perdidos } = useQuery({
    queryKey: ['ml-perdidos', filtros.data_ini, filtros.data_fim],
    queryFn: async () => (await api.get('/marketplace/ml/dias-perdidos', {
      params: { data_ini: filtros.data_ini, data_fim: filtros.data_fim },
    })).data,
    enabled: vista === 'perdidos',
  })

  const k = data?.kpis || ({} as Record<string, number>)
  const m = data?.modalidades || ({} as Record<string, number>)
  const linhasOrdenadas = useMemo(() => {
    const arr = [...(data?.linhas || [])]
    arr.sort((a: any, b: any) => {
      const va = a[sortKey], vb = b[sortKey]
      if (va == null) return 1
      if (vb == null) return -1
      if (va < vb) return sortDir === 'asc' ? -1 : 1
      if (va > vb) return sortDir === 'asc' ? 1 : -1
      return 0
    })
    return arr
  }, [data, sortKey, sortDir])

  function setF(k: string, v: any) {
    setFiltros((s) => ({ ...s, [k]: v }))
    setPage(1)
  }
  function setSort(col: keyof Linha) {
    if (sortKey === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else { setSortKey(col); setSortDir('desc') }
  }

  const exportCSV = () => {
    if (!data?.linhas?.length) return
    const cols = ['Anúncio', 'Conta', 'SKU', 'Data', 'Frete', 'Valor Unit', 'Qtde',
      'Faturamento', 'Custo', 'Imposto', 'Tarifa', 'Frete Comp', 'Frete Vend', 'MC R$', 'MC %']
    const linhas = data.linhas.map(l => [
      l.anuncio?.replace(/"/g, '""') || '',
      l.conta || '', l.sku || '',
      l.data?.slice(0, 10) || '',
      MODALIDADE_LABEL[l.modalidade || ''] || l.modalidade || '',
      l.valor_unit, l.qtde, l.faturamento, l.custo, l.imposto, l.tarifa,
      l.frete_comp, l.frete_vend, l.mc, l.mc_pct.toFixed(2),
    ])
    const csv = [cols.join(';'), ...linhas.map(r => r.map(c => `"${c}"`).join(';'))].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `resumo-financeiro-ml-${filtros.data_ini}_${filtros.data_fim}.csv`
    a.click()
  }

  const nivelarMut = useMutation({
    mutationFn: async (itens: any[]) =>
      (await api.post('/marketplace/ml/nivelar-custo', { itens })).data,
    onSuccess: (r) => {
      alert(`${r.atualizados} produto(s) atualizado(s).`)
      qc.invalidateQueries({ queryKey: ['ml-resumo'] })
      qc.invalidateQueries({ queryKey: ['ml-perdidos'] })
    },
  })

  return (
    <div className="p-3 lg:p-5 space-y-3 bg-slate-50 min-h-screen">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <DollarSign size={20} className="text-yellow-500" /> Resumo Financeiro ML
          </h1>
          <p className="text-xs text-ink-500">Margem por anúncio com rateio de tarifa e frete</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-ink-500">Conta:</span>
          <select className="input input-sm" value={filtros.canal_id} onChange={(e) => setF('canal_id', e.target.value)}>
            <option value="">Todas</option>
            {(contas || []).map(c => <option key={c.id} value={c.id}>{c.identificador}</option>)}
          </select>
        </div>
      </div>

      {/* ============ 5 KPIs grandes (estilo MT) ============ */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-2">
        <KpiBig
          cor="bg-slate-200 border-slate-400 text-slate-900"
          icon={<Wallet size={56} />}
          titulo="Vendas Aprovadas"
          valor={k.vendas_aprovadas}
          subs={[
            { label: 'Faturamento de Vendas', v: k.faturamento_vendas },
            { label: 'Faturamento de Frete Comprador', v: k.faturamento_frete_comp },
          ]}
          checkbox={{
            label: 'Considerar frete comprador',
            checked: filtros.considerar_frete_comp,
            onChange: (v) => setF('considerar_frete_comp', v),
          }}
        />
        <KpiBig
          cor="bg-rose-200 border-rose-400 text-rose-900"
          icon={<Package size={56} />}
          titulo="Custo & Imposto"
          valor={k.custo_imposto}
          subs={[
            { label: 'Custo', v: k.custo },
            { label: 'Imposto', v: k.imposto },
          ]}
        />
        <KpiBig
          cor="bg-amber-200 border-amber-400 text-amber-900"
          icon={<Tag size={56} />}
          titulo="Tarifa de Venda"
          valor={k.tarifa_venda}
          subs={[
            { label: 'Comissão', v: k.comissao },
            { label: 'Adiantamento', v: k.adiantamento },
          ]}
        />
        <KpiBig
          cor="bg-sky-200 border-sky-400 text-sky-900"
          icon={<Truck size={56} />}
          titulo="Frete Total"
          valor={k.frete_total}
          subs={[
            { label: 'Frete Comprador', v: k.frete_comp },
            { label: 'Frete Vendedor', v: k.frete_vend },
          ]}
        />
        <KpiBig
          cor="bg-emerald-200 border-emerald-400 text-emerald-900"
          icon={<Calculator size={56} />}
          titulo="Margem de Contribuição"
          valor={k.margem_contribuicao}
          destaquePct={k.margem_pct}
          subs={[]}
        />
      </div>

      {/* ============ 4 cards secundários ============ */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2">
        <CardBig icon={<Truck size={48} className="text-slate-400" />}>
          <div className="grid grid-cols-3 gap-2 w-full text-center">
            <SubItem label="Massa/Coleta" v={m.massa_coleta} />
            <SubItem label="Flex" v={m.flex} />
            <SubItem label="Full" v={m.full} />
            <SubItem label="N51" v={m.n51} />
            <SubItem label="Outros" v={m.outros} />
            <SubItem label="Total" v={m.total} bold />
          </div>
        </CardBig>
        <CardBig icon={<ListChecks size={48} className="text-slate-400" />}>
          <div className="grid grid-cols-3 gap-2 w-full text-center">
            <SubItem label="Qtd Vendas Aprovadas" v={k.qtd_aprovadas} fmt="num" sub={`${k.qtd_unidades || 0} unid.`} />
            <SubItem label="Qtd Total Vendas"
                     v={(k.qtd_aprovadas || 0) + (k.qtd_canceladas || 0)} fmt="num"
                     sub={`${k.qtd_unidades || 0} unid.`} />
            <SubItem label="Qtd Vendas Canceladas" v={k.qtd_canceladas} fmt="num" />
          </div>
        </CardBig>
        <CardBig icon={<DollarSign size={48} className="text-slate-400" />}>
          <div className="grid grid-cols-2 gap-2 w-full text-center">
            <SubItem label="Ticket Médio da MC das Vendas Aprovadas" v={k.ticket_mc} sub={`${(k.margem_pct || 0).toFixed(2)}%`} />
            <SubItem label="Ticket Médio" v={k.ticket_medio} />
          </div>
        </CardBig>
        <CardBig icon={<RotateCcw size={48} className="text-slate-400" />}>
          <div className="grid grid-cols-1 gap-2 w-full text-center">
            <SubItem label="Devoluções Parciais" v={k.devolucao_valor} sub={`${k.devolucao_qtd || 0} vendas`} />
          </div>
        </CardBig>
      </div>

      {/* ============ Filtrar Busca + Donut ============ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
        <div className="lg:col-span-2 bg-white border border-ink-200 rounded shadow-sm">
          <div className="px-3 py-2 border-b text-sm font-semibold text-ink-700">Filtrar Busca</div>
          <div className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Field label="Data Início"><input type="date" className="input input-sm" value={filtros.data_ini} onChange={(e) => setF('data_ini', e.target.value)} /></Field>
            <Field label="Data Fim"><input type="date" className="input input-sm" value={filtros.data_fim} onChange={(e) => setF('data_fim', e.target.value)} /></Field>
            <Field label="Nº Pedido / Carrinho"><input className="input input-sm" value={filtros.pedido} onChange={(e) => setF('pedido', e.target.value)} /></Field>
            <Field label="Título ou MLB"><input className="input input-sm" value={filtros.mlb} onChange={(e) => setF('mlb', e.target.value)} /></Field>
            <Field label="SKU"><input className="input input-sm" value={filtros.sku} onChange={(e) => setF('sku', e.target.value)} /></Field>
            <Field label="Status Venda">
              <select className="input input-sm" value={filtros.status} onChange={(e) => setF('status', e.target.value)}>
                <option value="">Todos</option>
                <option value="confirmado">Confirmado</option>
                <option value="rascunho">Rascunho</option>
                <option value="cancelado">Cancelado</option>
              </select>
            </Field>
            <Field label="Modalidade">
              <select className="input input-sm" value={filtros.modalidade} onChange={(e) => setF('modalidade', e.target.value)}>
                <option value="">Todos</option>
                <option value="fulfillment">Full</option>
                <option value="self_service">Flex</option>
                <option value="cross_docking">Coleta</option>
                <option value="drop_off">Agência</option>
                <option value="xd_drop_off">Agência XD</option>
              </select>
            </Field>
            <Field label="Tipo de Frete">
              <select className="input input-sm" disabled defaultValue="">
                <option value="">Todos</option>
              </select>
            </Field>
            <Field label="Publicidade">
              <select className="input input-sm" disabled defaultValue="">
                <option value="">Todos</option>
              </select>
            </Field>
            <Field label="Custo & Imposto">
              <select className="input input-sm" disabled defaultValue="">
                <option value="">Todos</option>
              </select>
            </Field>
            <Field label="Multicontas">
              {filtros.canal_id && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                  {(contas || []).find(c => String(c.id) === filtros.canal_id)?.identificador}
                  <button onClick={() => setF('canal_id', '')}>×</button>
                </span>
              )}
            </Field>
            <div className="flex items-end">
              <button className="btn btn-primary btn-sm w-full gap-1" onClick={() => refetch()}>
                <Search size={14} /> Buscar
              </button>
            </div>
          </div>
        </div>
        <div className="bg-white border border-ink-200 rounded shadow-sm">
          <div className="px-3 py-2 border-b text-sm font-semibold text-ink-700">Representação Gráfica</div>
          <div className="p-2">
            <ResponsiveContainer width="100%" height={210}>
              <PieChart>
                <Pie data={data?.donut || []} dataKey="valor" nameKey="label"
                     innerRadius={50} outerRadius={85}
                     label={(e: any) => {
                       const t = (data?.donut || []).reduce((s, x) => s + (x.valor || 0), 0)
                       return t ? `${((e.valor / t) * 100).toFixed(1)}%` : ''
                     }}
                     labelLine={false}>
                  {(data?.donut || []).map((d, i) => <Cell key={i} fill={d.cor} />)}
                </Pie>
                <Tooltip formatter={(v: any) => formatMoney(v)} />
                <Legend layout="vertical" verticalAlign="middle" align="right" wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="text-[10px] text-ink-400 px-2">
              * O frete pago pelo comprador não é considerado no gráfico e nas vendas aprovadas
            </div>
          </div>
        </div>
      </div>

      {/* ============ Botões de ação acima da tabela ============ */}
      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
        <div className="flex flex-wrap gap-1">
          <button className="btn btn-sm bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
                  onClick={() => setVista('perdidos')}>
            <Calculator size={13} /> Nivelar Custo & Imposto
          </button>
          <button className="btn btn-sm bg-emerald-600 hover:bg-emerald-700 text-white gap-1">
            <Tag size={13} /> Nivelar SKU
          </button>
          <button className="btn btn-sm bg-slate-700 hover:bg-slate-800 text-white gap-1"
                  onClick={() => setVista('ranking')}>
            <Trophy size={13} /> Ranking de Produtos
          </button>
          <button className="btn btn-sm bg-slate-700 hover:bg-slate-800 text-white gap-1"
                  onClick={() => setVista('perdidos')}>
            <AlertTriangle size={13} /> Rever Dias Perdidos
          </button>
          {vista !== 'tabela' && (
            <button className="btn btn-sm btn-outline gap-1" onClick={() => setVista('tabela')}>
              ← Voltar Detalhamento
            </button>
          )}
        </div>
        <div className="flex gap-1">
          <button className="btn btn-sm btn-outline gap-1" onClick={exportCSV}><FileSpreadsheet size={13} /> Excel</button>
          <button className="btn btn-sm btn-outline gap-1" onClick={exportCSV}><FileSpreadsheet size={13} /> CSV</button>
          <button className="btn btn-sm btn-outline gap-1" onClick={() => refetch()}><RefreshCw size={13} /></button>
        </div>
      </div>

      {/* ============ Tabela ============ */}
      {vista === 'tabela' && (
        <div className="bg-white border border-ink-200 rounded shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-3 py-1.5 bg-slate-100 text-xs text-ink-600 border-b">
            <span>{data ? `${Math.min((page - 1) * perPage + 1, data.paginacao.total)} a ${Math.min(page * perPage, data.paginacao.total)} de ${data.paginacao.total} registros` : '—'}</span>
            <div className="flex items-center gap-2">
              <button className="btn btn-xs" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>‹</button>
              <span>{page} / {data?.paginacao.pages || 1}</span>
              <button className="btn btn-xs" disabled={!data || page >= data.paginacao.pages} onClick={() => setPage(p => p + 1)}>›</button>
              <select className="input input-xs" value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1) }}>
                <option>25</option><option>50</option><option>100</option><option>200</option>
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-ink-600 border-b">
                <tr>
                  <ThSort col="anuncio" cur={sortKey} dir={sortDir} onClick={setSort}>Anúncio</ThSort>
                  <ThSort col="conta" cur={sortKey} dir={sortDir} onClick={setSort}>Conta</ThSort>
                  <ThSort col="sku" cur={sortKey} dir={sortDir} onClick={setSort}>SKU</ThSort>
                  <ThSort col="data" cur={sortKey} dir={sortDir} onClick={setSort}>Data</ThSort>
                  <ThSort col="modalidade" cur={sortKey} dir={sortDir} onClick={setSort}>Frete</ThSort>
                  <ThSort col="valor_unit" cur={sortKey} dir={sortDir} onClick={setSort} right>Valor Unit</ThSort>
                  <ThSort col="qtde" cur={sortKey} dir={sortDir} onClick={setSort} right>Qtde</ThSort>
                  <ThSort col="faturamento" cur={sortKey} dir={sortDir} onClick={setSort} right>Faturamento</ThSort>
                  <ThSort col="custo" cur={sortKey} dir={sortDir} onClick={setSort} right>Custo (-)</ThSort>
                  <ThSort col="imposto" cur={sortKey} dir={sortDir} onClick={setSort} right>Imposto (-)</ThSort>
                  <ThSort col="tarifa" cur={sortKey} dir={sortDir} onClick={setSort} right>Tarifa de Venda (-)</ThSort>
                  <ThSort col="frete_comp" cur={sortKey} dir={sortDir} onClick={setSort} right>Frete Comprador (+)</ThSort>
                  <ThSort col="frete_vend" cur={sortKey} dir={sortDir} onClick={setSort} right>Frete Vendedor (-)</ThSort>
                  <ThSort col="mc" cur={sortKey} dir={sortDir} onClick={setSort} right>Margem Contrib.</ThSort>
                  <ThSort col="mc_pct" cur={sortKey} dir={sortDir} onClick={setSort} right>MC %</ThSort>
                </tr>
              </thead>
              <tbody>
                {isLoading && <tr><td colSpan={15} className="text-center py-8 text-ink-400">Carregando…</td></tr>}
                {!isLoading && linhasOrdenadas.length === 0 && (
                  <tr><td colSpan={15} className="text-center py-8 text-ink-400">Sem vendas no período</td></tr>
                )}
                {linhasOrdenadas.map((l) => (
                  <tr key={l.item_id} className={`border-t hover:bg-slate-50 ${l.cancelado ? 'opacity-40 line-through' : ''}`}>
                    <td className="px-2 py-1 max-w-[280px] truncate">
                      {l.ml_item_id && (
                        <a href={`https://produto.mercadolivre.com.br/${l.ml_item_id.replace('MLB', 'MLB-')}`}
                           target="_blank" rel="noreferrer" className="text-blue-600 hover:underline mr-1">
                          <ExternalLink size={10} className="inline" />
                        </a>
                      )}
                      <span title={l.anuncio}>{l.anuncio}</span>
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap">{l.conta || '-'}</td>
                    <td className="px-2 py-1 font-mono whitespace-nowrap">{l.sku}</td>
                    <td className="px-2 py-1 whitespace-nowrap">{l.data?.slice(0, 10).split('-').reverse().join('/')}</td>
                    <td className="px-2 py-1 whitespace-nowrap">{MODALIDADE_LABEL[l.modalidade || ''] || l.modalidade || '-'}</td>
                    <td className="px-2 py-1 text-right">{formatMoney(l.valor_unit)}</td>
                    <td className="px-2 py-1 text-right">{l.qtde}</td>
                    <td className="px-2 py-1 text-right">{formatMoney(l.faturamento)}</td>
                    <td className="px-2 py-1 text-right text-rose-700">{formatMoney(l.custo)}</td>
                    <td className="px-2 py-1 text-right text-orange-700">{formatMoney(l.imposto)}</td>
                    <td className="px-2 py-1 text-right text-amber-700">{formatMoney(l.tarifa)}</td>
                    <td className="px-2 py-1 text-right text-sky-700">{formatMoney(l.frete_comp)}</td>
                    <td className="px-2 py-1 text-right text-sky-700">{formatMoney(l.frete_vend)}</td>
                    <td className="px-2 py-1 text-right font-semibold text-emerald-700">{formatMoney(l.mc)}</td>
                    <td className={`px-2 py-1 text-right font-semibold ${l.mc_pct < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                      {l.mc_pct.toFixed(2)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {vista === 'ranking' && (
        <div className="bg-white border border-ink-200 rounded shadow-sm overflow-x-auto">
          <div className="px-3 py-2 border-b font-semibold text-sm flex items-center gap-2">
            <Trophy size={14} className="text-yellow-600" /> Ranking de Produtos por Margem (período: {filtros.data_ini} a {filtros.data_fim})
          </div>
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-ink-600">
              <tr>
                <Th>#</Th><Th>SKU</Th><Th>Descrição</Th><Th>MLB</Th>
                <Th className="text-right">Qtd</Th>
                <Th className="text-right">Faturamento</Th>
                <Th className="text-right">Custo</Th>
                <Th className="text-right">Imposto</Th>
                <Th className="text-right">MC aprox.</Th>
                <Th className="text-right">MC %</Th>
              </tr>
            </thead>
            <tbody>
              {(ranking || []).map((r: any, i: number) => (
                <tr key={r.produto_id} className="border-t hover:bg-slate-50">
                  <td className="px-2 py-1">{i + 1}</td>
                  <td className="px-2 py-1 font-mono">{r.sku}</td>
                  <td className="px-2 py-1 max-w-[300px] truncate">{r.descricao}</td>
                  <td className="px-2 py-1 font-mono text-[10px]">{r.ml_item_id}</td>
                  <td className="px-2 py-1 text-right">{r.qtde}</td>
                  <td className="px-2 py-1 text-right">{formatMoney(r.faturamento)}</td>
                  <td className="px-2 py-1 text-right text-rose-700">{formatMoney(r.custo)}</td>
                  <td className="px-2 py-1 text-right text-orange-700">{formatMoney(r.imposto)}</td>
                  <td className="px-2 py-1 text-right font-semibold text-emerald-700">{formatMoney(r.mc_aproximada)}</td>
                  <td className={`px-2 py-1 text-right font-semibold ${r.mc_pct < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                    {r.mc_pct.toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {vista === 'perdidos' && (
        <div className="bg-white border border-ink-200 rounded shadow-sm">
          <div className="p-3 border-b flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold flex items-center gap-1">
                <AlertTriangle size={14} className="text-amber-600" />
                Produtos vendidos SEM custo cadastrado (Dias Perdidos)
              </div>
              <div className="text-xs text-ink-500">
                MC dessas vendas é fictícia. Cadastre o custo médio do produto pra contabilizar.
              </div>
            </div>
            <div className="text-xs text-ink-500">
              {(perdidos || []).length} produtos · faturamento: {' '}
              <span className="font-semibold text-rose-700">
                {formatMoney((perdidos || []).reduce((s: number, p: any) => s + p.faturamento_perdido, 0))}
              </span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-ink-600">
                <tr>
                  <Th>SKU</Th><Th>Descrição</Th><Th>MLB</Th>
                  <Th className="text-right">Vendas</Th>
                  <Th className="text-right">Unidades</Th>
                  <Th className="text-right">Faturamento perdido</Th>
                  <Th>Cadastrar custo</Th>
                </tr>
              </thead>
              <tbody>
                {(perdidos || []).map((p: any) => <LinhaPerdida key={p.produto_id} p={p} onSalvar={(c) => nivelarMut.mutate([{ produto_id: p.produto_id, custo: c }])} />)}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

function LinhaPerdida({ p, onSalvar }: { p: any; onSalvar: (custo: number) => void }) {
  const [valor, setValor] = useState('')
  return (
    <tr className="border-t hover:bg-slate-50">
      <td className="px-2 py-1 font-mono">{p.sku}</td>
      <td className="px-2 py-1 max-w-[300px] truncate">{p.descricao}</td>
      <td className="px-2 py-1 font-mono text-[10px]">{p.ml_item_id}</td>
      <td className="px-2 py-1 text-right">{p.qtd_vendas}</td>
      <td className="px-2 py-1 text-right">{p.qtd_unidades}</td>
      <td className="px-2 py-1 text-right text-rose-700 font-semibold">{formatMoney(p.faturamento_perdido)}</td>
      <td className="px-2 py-1 flex gap-1">
        <input type="number" step="0.01" placeholder="R$" className="input input-xs w-24"
               value={valor} onChange={(e) => setValor(e.target.value)} />
        <button className="btn btn-xs btn-primary" disabled={!valor}
                onClick={() => { if (valor) onSalvar(parseFloat(valor)) }}>Salvar</button>
      </td>
    </tr>
  )
}

/* ============ Componentes visuais (estilo MT) ============ */

function KpiBig({ cor, icon, titulo, valor, subs, destaquePct, checkbox }: {
  cor: string
  icon: React.ReactNode
  titulo: string
  valor: number
  subs: { label: string; v: number }[]
  destaquePct?: number
  checkbox?: { label: string; checked: boolean; onChange: (v: boolean) => void }
}) {
  return (
    <div className={`relative border-l-4 rounded shadow-sm overflow-hidden ${cor}`}>
      <div className="p-3 pr-16 min-h-[120px]">
        <div className="text-[11px] font-semibold uppercase tracking-wide opacity-80 mb-1">{titulo}</div>
        <div className="text-xl font-bold leading-tight">{formatMoney(valor)}</div>
        {destaquePct !== undefined && (
          <div className="text-sm font-semibold mt-0.5">{(destaquePct || 0).toFixed(2)}%</div>
        )}
        {subs.length > 0 && (
          <div className="mt-2 grid grid-cols-1 gap-0.5">
            {subs.map((s, i) => (
              <div key={i} className="flex justify-between text-[11px] opacity-90">
                <span className="truncate pr-2">{s.label}</span>
                <span className="font-semibold whitespace-nowrap">{formatMoney(s.v)}</span>
              </div>
            ))}
          </div>
        )}
        {checkbox && (
          <label className="flex items-center gap-1 mt-1 text-[11px] cursor-pointer opacity-90">
            <input type="checkbox" checked={checkbox.checked} onChange={(e) => checkbox.onChange(e.target.checked)} />
            {checkbox.label}
          </label>
        )}
      </div>
      <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-25">{icon}</div>
    </div>
  )
}

function CardBig({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="relative bg-white border border-ink-200 rounded shadow-sm overflow-hidden min-h-[100px]">
      <div className="p-3 pr-16 flex items-center min-h-[100px]">{children}</div>
      <div className="absolute right-3 top-1/2 -translate-y-1/2">{icon}</div>
    </div>
  )
}

function SubItem({ label, v, fmt, sub, bold }: { label: string; v: number | undefined; fmt?: 'num' | 'money'; sub?: string; bold?: boolean }) {
  return (
    <div className="text-center">
      <div className="text-[10px] text-ink-500 leading-tight">{label}</div>
      <div className={`text-sm leading-tight ${bold ? 'font-bold' : 'font-semibold'} text-ink-800`}>
        {fmt === 'num' ? (v || 0) : formatMoney(v || 0)}
      </div>
      {sub && <div className="text-[10px] text-ink-400 leading-tight">{sub}</div>}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-[11px] text-ink-500 block mb-0.5">{label}</label>
      {children}
    </div>
  )
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={`text-left px-2 py-1.5 font-medium ${className || ''}`}>{children}</th>
}

function ThSort({ col, cur, dir, onClick, children, right }: {
  col: keyof Linha; cur: keyof Linha; dir: 'asc' | 'desc';
  onClick: (c: keyof Linha) => void; children: React.ReactNode; right?: boolean
}) {
  return (
    <th className={`px-2 py-1.5 font-medium cursor-pointer select-none hover:bg-slate-100 ${right ? 'text-right' : 'text-left'}`}
        onClick={() => onClick(col)}>
      <span className={`flex items-center gap-0.5 ${right ? 'justify-end' : ''}`}>
        {children}
        {cur === col && (dir === 'asc' ? <ChevronUp size={11} /> : <ChevronDown size={11} />)}
      </span>
    </th>
  )
}
